import React from 'react';
import { Link } from 'react-router-dom';
import { EARNINGS, SOFTWARE_SKUS } from '../data/products';
import PageWorld from '../components/site/PageWorld';
import { PARTNERS } from '../data/worlds';

const fmt = (n) => '$' + n.toLocaleString('en-US');

// Partner path (telcos / ISPs / hosts). Host economics and operator
// transparency tables live here — deliberately OFF the product homepage,
// which sells agentOS pilots. Metal ownership sits with the partner.

const EDGE_SKUS = SOFTWARE_SKUS.filter((s) => s.sku.startsWith('Edge OS'));

export default function Partners() {
  return (
    <PageWorld world={PARTNERS}>
    <div className="cs-store" data-nova-note="Partners: the partner program and host economics — what a host earns running instances.">
      <header className="cs-store-hero">
        <h1>Partner with nventr — you own the metal, we license the OS</h1>
        <p>
          Edge / Cloud OS turns GPU nodes on business premises into an
          attested, placeable, metered inference fabric. The capital model is
          deliberate: telcos and ISPs own and deploy the nodes for strategic
          value — fiber retention, enterprise SLAs, jurisdiction-pinned
          sovereignty deals — while hosts lend the premises for zero capex and
          real earnings. nventr sells the software, not the hardware.
        </p>
        <div className="cs-land-cta-row cs-land-cta-center">
          <a
            href="mailto:partners@nventr.io?subject=Edge%20OS%20partnership"
            className="cs-btn cs-btn-primary"
          >
            Talk to us about Edge OS
          </a>
          <Link to="/plan" className="cs-btn cs-btn-outline">
            Partner sign-in
          </Link>
        </div>
      </header>

      <section className="cs-store-section">
        <h2>Edge OS license — per GPU, by attestation tier</h2>
        <p className="cs-store-note">
          The fee scales with the hardware class. Attestation tier sets the
          per-GPU rate.
        </p>
        <div className="cs-store-sw-grid">
          {EDGE_SKUS.map((s) => (
            <article key={s.sku} className="cs-sw-card">
              <h3>{s.sku}</h3>
              <div className="cs-sw-price">{s.price}</div>
              <p>{s.detail}</p>
              <small>{s.buyer}</small>
            </article>
          ))}
        </div>
      </section>

      <section className="cs-store-section" id="earnings">
        <h2>Host economics — zero capex, real earnings</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>{EARNINGS.intro}</p>

        <div className="cs-earn-grid">
          <div className="cs-earn-card">
            <h3>Every business host gets</h3>
            <ul className="cs-earn-fixed">
              {EARNINGS.fixed.map((f) => (
                <li key={f.label}>
                  <span>{f.label}</span>
                  <strong>{f.value}</strong>
                </li>
              ))}
            </ul>
          </div>

          <div className="cs-earn-card cs-earn-wide">
            <h3>+ Revenue share by utilization &amp; token lane</h3>
            <p className="cs-earn-sub">
              Annual revenue-share dollars per node (12% of node gross).
              Each cell: commodity $0.25/M · sovereignty $0.40/M.
            </p>
            <div className="cs-plan-tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>Node config</th>
                    {EARNINGS.utilTiers.map((t) => (
                      <th key={t.id}>{t.label}</th>
                    ))}
                    <th>+ Thermal kit</th>
                    <th>Total @ baseline*</th>
                  </tr>
                </thead>
                <tbody>
                  {EARNINGS.hostConfigs.map((c) => (
                    <tr key={c.config}>
                      <td>
                        <strong>{c.sku}</strong>
                        <br />
                        <small>{c.config}</small>
                      </td>
                      {EARNINGS.utilTiers.map((t) => (
                        <td key={t.id}>
                          {fmt(c.share[t.id][0])} · <strong>{fmt(c.share[t.id][1])}</strong>
                        </td>
                      ))}
                      <td>+{fmt(c.thermal)}/yr</td>
                      <td>
                        <strong>{c.totalBaseline}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="cs-earn-sub">
              *Total host position at 50% utilization, commodity lane: floor +
              fiber value + share (thermal kit additional).
            </p>
          </div>

          <div className="cs-earn-card">
            <h3>{EARNINGS.home.sku} — opt-in only</h3>
            <p className="cs-earn-sub">{EARNINGS.home.note}</p>
            <ul className="cs-earn-fixed">
              {EARNINGS.home.lines.map((l) => (
                <li key={l.label}>
                  <span>{l.label}</span>
                  <strong>{l.value}</strong>
                </li>
              ))}
            </ul>
          </div>

          <div className="cs-earn-card cs-earn-wide">
            <h3>Owning the metal — operator economics, in the open</h3>
            <p className="cs-earn-sub">{EARNINGS.operator.note}</p>
            <div className="cs-plan-tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>Build</th>
                    <th>Margin/yr @ $0.25/M</th>
                    <th>Margin/yr @ $0.40/M</th>
                    <th>Payback (before OS fee)</th>
                  </tr>
                </thead>
                <tbody>
                  {EARNINGS.operator.rows.map((r) => (
                    <tr key={r.config}>
                      <td>{r.config}</td>
                      <td>{r.commodity}</td>
                      <td>
                        <strong>{r.sovereignty}</strong>
                      </td>
                      <td>{r.payback}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="cs-earn-sub">{EARNINGS.operator.readthrough}</p>
          </div>
        </div>

        <div className="cs-earn-card cs-earn-wide" style={{ marginTop: 18 }}>
          <h3>What the OS fee takes — owner ROI by use case &amp; hardware tier</h3>
          <p className="cs-earn-sub">{EARNINGS.osImpact.intro}</p>
          <div className="cs-plan-tablewrap">
            <table>
              <thead>
                <tr>
                  <th>Build</th>
                  {EARNINGS.osImpact.scenarios.map((s) => (
                    <th key={s}>{s}</th>
                  ))}
                  <th>Breakeven OS fee</th>
                </tr>
              </thead>
              <tbody>
                {EARNINGS.osImpact.rows.map((r) => (
                  <tr key={r.build}>
                    <td>{r.build}</td>
                    {r.cells.map((c, i) => (
                      <td key={i}>
                        <strong>{c.net}</strong> net
                        <br />
                        <small>{c.take} · {c.roi}</small>
                      </td>
                    ))}
                    <td>{r.breakeven}</td>
                  </tr>
                ))}
                <tr>
                  <td>{EARNINGS.osImpact.home.label}</td>
                  <td colSpan={4}>
                    <small>{EARNINGS.osImpact.home.detail}</small>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="cs-earn-sub">{EARNINGS.osImpact.readthrough}</p>
        </div>

        <p className="cs-earn-assumptions">{EARNINGS.assumptions}</p>
      </section>

      <footer className="cs-store-footer">
        Figures are directional, pre-pilot, derived from the plan&rsquo;s
        stated assumptions. Contact nventr for partner terms.
      </footer>
    </div>
    </PageWorld>
  );
}
